import type { CSSProperties } from 'react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

export interface SelectOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

// Dropdown customizado (trigger + lista) pra substituir <select> nativo, cujo popup
// é renderizado pelo OS/browser e não segue o tema do app. Suporta teclado (setas,
// enter, escape) e fecha ao clicar fora.
export function Select<T extends string>({
  value, options, onChange, placeholder = 'Selecione…', ariaLabel, className = '', compact, disabled,
}: {
  value: T | null
  options: SelectOption<T>[]
  onChange: (value: T) => void
  placeholder?: string
  ariaLabel?: string
  className?: string
  compact?: boolean
  disabled?: boolean
}) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [listStyle, setListStyle] = useState<CSSProperties>()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selected = options.find((o) => o.value === value)
  const selectedIndex = options.findIndex((o) => o.value === value)
  const isDisabled = disabled || !options.some((o) => !o.disabled)
  const isPlaceholder = !selected || selected.disabled
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined

  const firstEnabledIndex = () => options.findIndex((o) => !o.disabled)
  const nextEnabledIndex = (from: number, dir: 1 | -1) => {
    for (let i = from + dir; i >= 0 && i < options.length; i += dir) {
      if (!options[i].disabled) {return i}
    }
    return from >= 0 && !options[from]?.disabled ? from : firstEnabledIndex()
  }

  const updateListStyle = () => {
    const anchor = rootRef.current?.closest('.fz-attendant') as HTMLElement | null
    const rect = (anchor ?? triggerRef.current)?.getBoundingClientRect()
    if (!rect) {return}
    const margin = 8
    const maxWidth = Math.max(180, window.innerWidth - margin * 2)
    const preferredMaxWidth = Math.min(360, maxWidth)
    const spaceBelow = window.innerHeight - rect.bottom - margin
    const spaceAbove = rect.top - margin
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow
    const maxHeight = Math.max(160, Math.min(260, (openUp ? spaceAbove : spaceBelow) - 6))

    // Alinha pela esquerda do trigger por padrão. Só alinha pela direita quando a lista,
    // na sua largura MÁXIMA possível (preferredMaxWidth), estouraria a viewport — clampar
    // `left` incondicionalmente empurrava a lista pra longe do campo mesmo quando o
    // conteúdo real (minWidth: rect.width) era bem mais estreito que preferredMaxWidth.
    const fitsLeftAligned = rect.left + preferredMaxWidth + margin <= window.innerWidth
    // Mesmo alinhado pela direita, `right` precisa de teto: se o conteúdo real chegar perto
    // de preferredMaxWidth, o edge esquerdo da lista (innerWidth - right - width) não pode
    // ficar negativo em viewports estreitas (mobile é o device primário deste app).
    const maxRight = Math.max(margin, window.innerWidth - preferredMaxWidth - margin)
    const right = Math.min(Math.max(margin, window.innerWidth - rect.right), maxRight)

    setListStyle({
      position: 'fixed',
      ...(fitsLeftAligned
        ? { left: Math.max(margin, rect.left), right: 'auto' }
        : { left: 'auto', right }),
      minWidth: rect.width,
      maxWidth: preferredMaxWidth,
      maxHeight,
      ...(openUp
        ? { top: 'auto', bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6, bottom: 'auto' }),
    })
  }

  useEffect(() => {
    if (!open) {return}
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) {return}
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  useLayoutEffect(() => {
    if (!open) {return}
    updateListStyle()
    const onReposition = () => updateListStyle()
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [open, options])

  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : firstEnabledIndex())
    }
  }, [open, options, value])

  const commit = (opt: SelectOption<T>) => {
    if (opt.disabled) {return}
    onChange(opt.value)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) {return}
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => nextEnabledIndex(i, 1)); return }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => nextEnabledIndex(i, -1)); return }
    if (e.key === 'Home') { e.preventDefault(); setActiveIndex(firstEnabledIndex()); return }
    if (e.key === 'End') { e.preventDefault(); setActiveIndex(nextEnabledIndex(options.length, -1)); return }
    if (e.key === 'Enter') { e.preventDefault(); const opt = options[activeIndex]; if (opt) {commit(opt);} return }
    if (e.key === 'Tab') {setOpen(false)}
  }

  const list = open ? (
    <ul
      id={listId}
      ref={listRef}
      className="fz-select__list"
      role="listbox"
      aria-label={ariaLabel}
      style={listStyle}
    >
      {options.map((opt, i) => {
        const optionSelected = opt.value === value && !opt.disabled
        return (
          <li
            id={`${listId}-${i}`}
            key={opt.value}
            role="option"
            aria-selected={optionSelected}
            aria-disabled={opt.disabled || undefined}
            className={'fz-select__opt '
              + (i === activeIndex ? 'is-active ' : '')
              + (optionSelected ? 'is-selected ' : '')
              + (opt.disabled ? 'is-disabled' : '')}
            onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
            onClick={() => commit(opt)}
          >
            <span>{opt.label}</span>
            {optionSelected && <Icon name="check" size={13} />}
          </li>
        )
      })}
    </ul>
  ) : null

  return (
    <div className={'fz-select ' + (compact ? 'fz-select--compact ' : '') + className} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="fz-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeOption ? `${listId}-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        disabled={isDisabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={'fz-select__value ' + (isPlaceholder ? 'is-placeholder' : '')}>{selected?.label ?? placeholder}</span>
        <Icon name="chevD" size={12} className="fz-select__arrow" />
      </button>
      {list && createPortal(list, document.body)}
    </div>
  )
}
